(() => {
  'use strict';
  if (window.__liaCardsV2) return;
  window.__liaCardsV2 = true;
  const debug = new URLSearchParams(location.search).get('lia_tracking_debug') === '1';
  if ((window.Shopify?.designMode || window.Shopify?.theme?.role !== 'main') && !debug) return;
  const selector = 'product-item, [data-article-product-card], .product-recommendation-cards > div';
  const records = new Map();
  const seen = new Set();
  const clicked = new Set();
  const pending = new Set();
  const visible = new Set();
  const timers = new Map();
  const ttl = 30 * 60 * 1000;
  const storageKey = 'lia_card_clicks_v2';
  const pagePath = location.pathname;
  const article = (document.querySelector('[data-lia-blog-tracking]')?.dataset.liaBlogTracking || '').split('/').filter(Boolean).pop() || '';
  const locale = document.documentElement.lang || 'en';
  let observer;
  let scanQueued = false;

  function allowed() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function handle(href) {
    try {
      const url = new URL(href, location.origin);
      if (url.origin !== location.origin) return '';
      return url.pathname.match(/\/products\/([a-z0-9-]+)\/?$/)?.[1] || '';
    } catch (_) { return ''; }
  }
  function readContexts() {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
      return Object.fromEntries(Object.entries(value).filter(([, c]) =>
        c && Number.isFinite(c.at) && Date.now() - c.at >= 0 && Date.now() - c.at < ttl));
    } catch (_) { return {}; }
  }
  function remember(data) {
    if (!allowed()) return;
    try {
      const contexts = readContexts();
      contexts[data.product_handle] = { ...data, at: Date.now() };
      const entries = Object.entries(contexts).sort((a, b) => b[1].at - a[1].at).slice(0, 50);
      sessionStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(entries)));
    } catch (_) {}
  }
  function payload(record, extra = {}) {
    return { ...record.data, article_handle: article, locale, source_path: pagePath,
      tracking_version: 'cards_v2', debug_mode: debug, ...extra };
  }
  async function publish(name, data) {
    if (!allowed() || typeof window.Shopify?.analytics?.publish !== 'function') return false;
    if (debug) console.info('[Lia cards]', name, data);
    try { return (await window.Shopify.analytics.publish('lia:' + name, data)) !== false; }
    catch (_) { return false; }
  }
  async function expose(record, reason) {
    if (seen.has(record.key)) return true;
    if (pending.has(record.key)) return false;
    pending.add(record.key);
    const ok = await publish('product_card_view', payload(record, { impression_reason: reason }));
    pending.delete(record.key);
    if (ok) seen.add(record.key);
    return ok;
  }
  function cancel(card) {
    clearTimeout(timers.get(card));
    timers.delete(card);
  }
  function schedule(card) {
    const record = records.get(card);
    if (!record || seen.has(record.key) || timers.has(card) || document.hidden || !allowed()) return;
    timers.set(card, setTimeout(() => {
      timers.delete(card);
      if (!document.hidden && visible.has(card) && card.isConnected) void expose(record, 'visible_1s');
    }, 1000));
  }
  function refresh() {
    if (!allowed() || document.hidden) {
      timers.forEach((_, card) => cancel(card));
      if (!allowed()) {
        try { sessionStorage.removeItem(storageKey); } catch (_) {}
      }
      return;
    }
    visible.forEach(schedule);
  }
  function register(card) {
    if (records.has(card)) return;
    const link = card.matches('a[href]') ? card : card.querySelector('a[href*="/products/"]');
    const product = link && handle(link.href);
    if (!product) return;
    const section = card.closest('[id^="shopify-section-"]');
    const list = card.closest('.product-recommendation-cards, product-list, .product-list');
    const sectionId = section?.id?.replace(/^shopify-section-/, '') || 'content';
    const surface = card.closest('[data-lia-blog-tracking]') ? 'article' :
      document.body.className.match(/template-([a-z-]+)/)?.[1] || 'storefront';
    const listIndex = section && list ? Array.from(section.querySelectorAll('.product-recommendation-cards, product-list, .product-list')).indexOf(list) + 1 : 1;
    const listId = (surface + ':' + sectionId + ':' + listIndex).slice(0, 100);
    const siblings = list ? Array.from(list.querySelectorAll(selector)) : [];
    const position = siblings.includes(card) ? siblings.indexOf(card) + 1 : records.size + 1;
    const key = listId + ':' + product;
    records.set(card, { key, data: { product_handle: product, card_position: position,
      card_list_id: listId, card_surface: surface } });
    observer?.observe(card);
  }
  function scan() {
    scanQueued = false;
    records.forEach((_, card) => {
      if (!card.isConnected) {
        observer?.unobserve(card);
        cancel(card);
        visible.delete(card);
        records.delete(card);
      }
    });
    document.querySelectorAll(selector).forEach(register);
    document.querySelectorAll('[data-lia-blog-tracking] a[href*="/products/"]').forEach(link => {
      if (link.querySelector('img') && !link.closest(selector)) register(link);
    });
  }
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          visible.add(entry.target);
          schedule(entry.target);
        } else {
          visible.delete(entry.target);
          cancel(entry.target);
        }
      });
    }, { threshold: [0, 0.5] });
  }
  function onClick(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;
    const target = event.target?.closest?.('a[href], button');
    if (!target || !allowed()) return;
    const card = target.closest(selector) || target.closest('[data-lia-blog-tracking] a[href]');
    const record = records.get(card);
    if (!record) return;
    const isProductLink = target.matches('a[href]') && handle(target.href) === record.data.product_handle;
    const isQuickBuy = target.matches('button[type="submit"], button[aria-controls^="product-"]');
    if (!isProductLink && !isQuickBuy) return;
    const data = payload(record, { interaction_type: isProductLink ? 'product_link' : 'quick_buy' });
    remember(data);
    if (clicked.has(record.key)) return;
    clicked.add(record.key);
    void expose(record, 'interaction');
    void publish('product_card_click', data).then(ok => { if (!ok) clicked.delete(record.key); });
  }
  scan();
  const mutations = new MutationObserver(() => {
    if (!scanQueued) {
      scanQueued = true;
      requestAnimationFrame(scan);
    }
  });
  mutations.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('visitorConsentCollected', refresh);
  let attempts = 0;
  const retry = setInterval(() => {
    refresh();
    if (++attempts >= 30 || (allowed() && window.Shopify?.analytics?.publish)) clearInterval(retry);
  }, 1000);
  window.addEventListener('pagehide', () => {
    clearInterval(retry);
    timers.forEach((_, card) => cancel(card));
  });
  window.addEventListener('pageshow', refresh);
})();

